import React, { useEffect, useState } from "react";
import axios from "axios";
import { Card, Input, Button, Typography, message, App, Modal, Tag } from "antd";
import { EyeInvisibleOutlined, EyeTwoTone, ArrowLeftOutlined } from "@ant-design/icons";
import "./LoginPage.css";
import { useLocation, useNavigate } from "react-router-dom";
import { GoogleLogin } from "@react-oauth/google";
import { jwtDecode } from "jwt-decode"; // Install via: npm install jwt-decode
import { getPostAuthRoute } from "../../utils/navigation";

const { Title, Text, Link } = Typography;

const LoginPage = () => {
  const { notification } = App.useApp();
  const navigate = useNavigate();
  const location = useLocation();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [accountSelection, setAccountSelection] = useState(null);
  const [selectingAccount, setSelectingAccount] = useState(false);
  const redirectPath = getPostAuthRoute(location.state?.toolName);

  useEffect(() => {
    if (localStorage.getItem("token")) {
      navigate(redirectPath, { replace: true });
    }
  }, [navigate, redirectPath]);

  const handleGoogleLoginSuccess = async (credentialResponse) => {
    try {
      const decoded = jwtDecode(credentialResponse.credential);
      console.log(decoded);
      // Optional: Send to backend for token exchange/login
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/api/Auth/google-login`,
        {
          IdToken: credentialResponse.credential,
        }
      );

      if (
        (response.data.data || {}).credentials &&
        response.data.data.credentials.token
      ) {
        localStorage.setItem("token", response.data.data.credentials.token);
        localStorage.setItem("user", JSON.stringify(response.data.data));
        navigate(redirectPath);
        message.success("Login successful!");
      }
    } catch (error) {
      console.error("Google login error:", error);
      message.error("Login failed");
    }
  };

  const completeLogin = (data = {}) => {
    const token = data.credentials?.token || data.token;
    if (!token) return false;

    localStorage.setItem("token", token);
    localStorage.setItem("user", JSON.stringify(data));
    message.success("Login successful!");

    const userType = String(data.userType || data.accountType || "").toLowerCase();
    if (userType === "staff" && data.forceChangePassword) {
      navigate("/staff/change-password", { replace: true });
      return true;
    }

    navigate(userType === "staff" ? "/dashboard/business/dynamic-qr" : redirectPath, { replace: true });
    return true;
  };

  const handleLogin = async () => {
    if (!username || !password) {
      message.warning("Please enter both username and password");
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/api/Auth/login`,
        {
          Username: username,
          Password: password,
        }
      );

      const data = response.data?.data || {};
      if (data.requiresAccountSelection) {
        setAccountSelection({
          loginSessionToken: data.loginSessionToken,
          accounts: Array.isArray(data.accounts) ? data.accounts : [],
        });
        return;
      }

      if (!completeLogin(data)) {
        message.error(response.data?.message || "Unable to login.");
      }
    } catch (error) {
      console.error("Login failed:", error);

      const errorMsg =
        error.response?.data?.message || "Something went wrong during login.";
      console.log(errorMsg);
      notification.warning({
        message: errorMsg,
      });
      console.log("ok");
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAccount = async (account) => {
    if (!accountSelection?.loginSessionToken || !account) return;
    setSelectingAccount(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/api/Auth/select-account`,
        {
          loginSessionToken: accountSelection.loginSessionToken,
          accountType: account.accountType,
          accountId: account.accountId,
        }
      );
      const data = response.data?.data || {};
      if (data.requiresAccountSelection) {
        message.error("Please select an account to continue.");
        return;
      }
      if (completeLogin(data)) {
        setAccountSelection(null);
      } else {
        message.error(response.data?.message || "Unable to select account.");
      }
    } catch (error) {
      message.error(error.response?.data?.message || "Unable to select account.");
    } finally {
      setSelectingAccount(false);
    }
  };

  return (
    <div className="login-container">
      <Card className="login-card">
        <Button
          type="link"
          size="large"
          onClick={() => navigate(-1)}
          style={{
            padding: 0,
            marginBottom: 12,
            display: "flex",
            alignItems: "center",
            color: 'black'
          }}
        >
          <ArrowLeftOutlined />
        </Button>

        <div className="logo-container">
          <img src={require("../../assets/logo.png")} alt="logo" />
        </div>
        <Title level={3}>Sign In</Title>
        <Text type="secondary">Simplify Your PDF Tasks Effortlessly</Text>

        <div className="form-item">
          <Text strong>Email or Username</Text>
          <Input
            placeholder="Enter your email or username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
          />
        </div>

        <div className="form-item">
          <Text strong>Password</Text>
          <Input.Password
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            iconRender={(visible) =>
              visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />
            }
          />
        </div>

        <div className="forgot-password">
          <Link href="/forgotpass">Forgot Password?</Link>
        </div>

        <Button
          type="primary"
          block
          className="login-button"
          onClick={handleLogin}
          loading={loading}
        >
          Login
        </Button>
        {process.env.REACT_APP_GOOGLE_CLIENT_ID ? (
          <div style={{ marginTop: "16px", textAlign: "center" }}>
            <GoogleLogin
              onSuccess={handleGoogleLoginSuccess}
              onError={() => message.error("Google login failed")}
            />
          </div>
        ) : (
          []
        )}

        <div className="signup-text">
          New on our platform? <Link href="/register">Create an account</Link>
        </div>
      </Card>

      <Modal
        open={Boolean(accountSelection)}
        title="Choose Account"
        footer={null}
        onCancel={() => setAccountSelection(null)}
        centered
      >
        <div className="account-select-list">
          {(accountSelection?.accounts || []).map((account) => (
            <button
              type="button"
              className="account-select-card"
              key={`${account.accountType}-${account.accountId}`}
              onClick={() => handleSelectAccount(account)}
              disabled={selectingAccount}
            >
              <div>
                <strong>{account.displayName || account.email || account.accountType}</strong>
                <span>{account.email}</span>
              </div>
              <Tag color={account.accountType === "Staff" ? "purple" : "green"}>{account.accountType}</Tag>
            </button>
          ))}
        </div>
      </Modal>
    </div>
  );
};

export default LoginPage;
